import { ESRI } from '@rsos/constants/mapProviders';
import {
  MARKER_TYPE_GRAPHIC,
  MARKER_TYPE_TEXT,
} from '@rsos/constants/markerTypes';
import { createGraphic } from '../../providers/esri/graphics/graphic/createGraphic';
import hideMarker from './hideMarker';
import removeMarker from './removeMarker';
import showMarker from './showMarker';
import updateMarker from './updateMarker';

/**
 * Creates and returns a marker instance based on the map provider
 * @param {Object} mapObject - map object
 * @param {Object} options - options for the marker
 * @param {Object} options.point - the coordinates to draw the marker
 * @param {Object} [options.icon] - the svg icon used for the marker graphic
 * @param {Object} [options.iconSize]
 * @param {Number} [options.iconSize.height] - height in px
 * @param {Number} [options.iconSize.width] - width in px
 * @param {Object} [options.color] - color of text symbol
 * @param {Object} [options.haloColor] - color of the text symbol's halo
 * @param {Object} [options.haloSize] - size in points of the text symbol's halo
 * @param {Object} [options.text] -
 * @param {Object} [options.custom] - custom key/value pairs to include
 * @param {Object} options.markerType - value of MARKER_TYPE_TEXT or MARKER_TYPE_GRAPHIC
 * @param {Function} [callback] - callback function
 */
const createMarker = ({ mapObject, options = {}, markerType, callback }) => {
  switch (mapObject.mapProvider) {
    case ESRI: {
      let configs = {
        point: {
          y: options.point.lat,
          x: options.point.lng,
        },
        symbol: {},
      };
      if (markerType === MARKER_TYPE_GRAPHIC) {
        configs.symbol = {
          type: 'picture-marker',
          url: options.icon,
          height: `${options?.iconSize?.height ? options.iconSize.height : 30}`,
          width: `${options?.iconSize?.width ? options.iconSize.width : 30}`,
        };
      } else if (markerType === MARKER_TYPE_TEXT) {
        configs.symbol = {
          type: 'text', // autocasts as new TextSymbol()
          color: options.color,
          haloColor: options.haloColor,
          haloSize: options.haloColor,
          text: options.text,
          xoffset: 3,
          yoffset: 3,
          font: {
            // autocasts as new Font()
            size: 14,
            // TODO: Check with design for what font to use. List of supported fonts for 2D MapView doesn't include `Roboto`. https://developers.arcgis.com/javascript/latest/labeling/
            family: 'Noto Sans',
          },
        };
      } else {
        throw new Error('Incorrect marker type');
      }
      return createGraphic({
        mapObject,
        configs,
      }).then(simpleMarkerSymbol => {
        const marker = {
          instance: simpleMarkerSymbol.graphic,
          hide: () => {
            hideMarker(mapObject, simpleMarkerSymbol);
          },
          remove: () => {
            removeMarker(mapObject, marker, options.layer);
          },
          show: () => {
            showMarker(mapObject, simpleMarkerSymbol);
          },
          changeMarkerIcon: opts => {
            updateMarker({
              mapObject,
              marker: simpleMarkerSymbol.graphic,
              options: opts,
            });
          },
          updatePosition: opts => {
            updateMarker({
              mapObject,
              marker: simpleMarkerSymbol,
              options: opts,
            });
          },
          ...(options.custom ? { custom: { ...options.custom } } : {}),
        };
        if (callback) {
          callback(marker);
        } else {
          return marker;
        }
      });
    }
    default:
      return null;
  }
};

export default createMarker;
